// A screen stack, not a page router. `04 Daily coop` is the root; every other
// screen is pushed over it and Android/browser back pops it (PLAN-03 §4).
//
// The element type is a parameter so the stack logic can be tested without a
// DOM — the vitest environment here is node, and the router is worth testing
// more than the transitions are worth a browser.

import type { GameStateStore } from '../core/store';
import type { GameSession } from './game';
import type { Shell } from './shell';

export type RouteParams = Record<string, string>;

export interface ScreenServices {
  store: GameStateStore;
  shell: Shell;
  /** The sim and the Pixi world. Boot lazily: onboarding never draws the yard. */
  game: GameSession;
}

export interface ScreenContext<E> {
  readonly params: RouteParams;
  readonly router: Router<E>;
  readonly services: ScreenServices;
}

export interface Screen<E> {
  /** Stable id; also the route name in the history hash. */
  readonly id: string;
  /** Builds the screen. Called once per push, not per state change. */
  render(ctx: ScreenContext<E>): E;
  /** Called when the screen leaves the stack for good. */
  dispose?(): void;
  /**
   * True when this screen lets the Pixi world show through. Anything opaque
   * pauses the renderer, which is most of the battery on a phone (PLAN-03 §4).
   */
  readonly showsStage?: boolean;
}

export type Transition = 'forward' | 'back' | 'replace' | 'root';

export interface MountedScreen<E> {
  readonly id: string;
  readonly params: RouteParams;
  readonly element: E;
  readonly showsStage: boolean;
}

export interface RouterHost<E> {
  mount(entry: MountedScreen<E>, transition: Transition): void;
  unmount?(entry: MountedScreen<E>): void;
}

export interface Route {
  readonly id: string;
  readonly params: RouteParams;
  readonly depth: number;
}

export interface HistoryPort {
  push(url: string): void;
  replace(url: string): void;
  back(): void;
}

interface StackEntry<E> {
  screen: Screen<E>;
  params: RouteParams;
}

/** `?ui=v2#/bird?chicken=chk_1` — the query lives inside the hash so the
 *  document URL stays the one the v2 dev route booted from. */
export function routeUrl(id: string, params: RouteParams): string {
  const query = new URLSearchParams(params).toString();
  return `#/${id}${query ? `?${query}` : ''}`;
}

export class Router<E = unknown> {
  private stack: Array<StackEntry<E>> = [];
  private mounted: MountedScreen<E> | null = null;
  /** The screen object `mounted` was built from, so it can be disposed. */
  private live: Screen<E> | null = null;
  private onChange: (route: Route) => void;

  constructor(
    private readonly services: ScreenServices,
    private readonly host: RouterHost<E>,
    /** Injected so tests drive the stack without a browser. */
    private readonly history: HistoryPort | null = null,
  ) {
    this.onChange = () => undefined;
  }

  /** Registers the change listener and returns the unsubscribe. */
  subscribe(listener: (route: Route) => void): () => void {
    this.onChange = listener;
    return () => {
      if (this.onChange === listener) this.onChange = () => undefined;
    };
  }

  get current(): Route | null {
    if (!this.mounted) return null;
    return { id: this.mounted.id, params: this.mounted.params, depth: this.stack.length };
  }

  get depth(): number {
    return this.stack.length;
  }

  /** False on the root screen, where back leaves the game. */
  get canGoBack(): boolean {
    return this.stack.length > 1;
  }

  push(screen: Screen<E>, params: RouteParams = {}): void {
    this.stack.push({ screen, params });
    this.history?.push(routeUrl(screen.id, params));
    this.present('forward');
  }

  /** Swaps the top entry — the root swap after onboarding finishes. */
  replace(screen: Screen<E>, params: RouteParams = {}): void {
    this.stack[this.stack.length - 1] = { screen, params };
    this.history?.replace(routeUrl(screen.id, params));
    this.present('replace');
  }

  /**
   * Clears the stack down to one screen. Used when a new game replaces the old
   * one, where every onboarding screen behind it is meaningless.
   */
  reset(screen: Screen<E>, params: RouteParams = {}): void {
    this.truncate(0);
    this.stack.push({ screen, params });
    this.history?.replace(routeUrl(screen.id, params));
    this.present('root');
  }

  /**
   * Goes back one screen. Through the history API when there is one, so the
   * browser's own forward button behaves; the popstate handler finishes the job.
   */
  back(): boolean {
    if (!this.canGoBack) return false;
    if (this.history) this.history.back();
    else this.handlePop();
    return true;
  }

  /** Call from `popstate`. Returns true when it popped a screen. */
  handlePop(): boolean {
    if (!this.canGoBack) return false;
    this.truncate(this.stack.length - 1);
    this.present('back');
    return true;
  }

  /**
   * Drops every entry above `depth` and disposes the screens that left with
   * them. A screen that is still on the stack keeps its subscriptions — it is
   * about to be shown again.
   */
  private truncate(depth: number): void {
    const leaving = this.stack.splice(depth);
    const live = this.live;
    // The screen on screen is always one of the ones leaving, so it is disposed
    // here rather than by the caller.
    this.detach(true);
    for (const entry of leaving) {
      if (entry.screen === live) continue;
      entry.screen.dispose?.();
    }
  }

  /** Detaches whatever is on screen. Idempotent. */
  private detach(disposeLive: boolean): void {
    if (this.mounted) this.host.unmount?.(this.mounted);
    this.mounted = null;
    if (this.live && disposeLive) this.live.dispose?.();
    this.live = null;
  }

  private present(transition: Transition): void {
    const top = this.stack[this.stack.length - 1];
    if (!top) return;
    const liveStays = this.live !== null && this.stack.some((entry) => entry.screen === this.live);
    this.detach(!liveStays);
    const element = top.screen.render({ params: top.params, router: this, services: this.services });
    this.live = top.screen;
    this.mounted = {
      id: top.screen.id,
      params: top.params,
      element,
      showsStage: top.screen.showsStage === true,
    };
    this.host.mount(this.mounted, transition);
    const route = this.current;
    if (route) this.onChange(route);
  }
}

/**
 * Wires the browser back button. Returns an unsubscribe, or a no-op when there
 * is no window (tests, SSR-ish environments).
 */
export function bindBrowserBack<E>(router: Router<E>, target: EventTarget | null = globalThis.window ?? null): () => void {
  if (!target) return () => undefined;
  const handler = (): void => {
    router.handlePop();
  };
  target.addEventListener('popstate', handler);
  return () => target.removeEventListener('popstate', handler);
}
